// Ollama integration for RAG with Llama3.2:latest
class OllamaService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_OLLAMA_URL || 'http://localhost:11434';
    this.model = 'llama3.2:latest';
  }

  // Check if Ollama is available
  async isAvailable() {
    try {
      const response = await fetch(`${this.baseUrl}/api/tags`);
      return response.ok;
    } catch (error) {
      console.error('Ollama not available:', error);
      return false;
    }
  }

  // Alias for backward compatibility
  async checkConnection() {
    return this.isAvailable();
  }

  // Generate streaming response using RAG
  async generateStreamingResponse(userMessage, context = [], language = 'en', onChunk, systemSettings = null) {
    try {
      console.log('🦙 Ollama: Starting streaming response generation');
      console.log('📝 User message:', userMessage);
      console.log('📚 Context items:', context.length);
      console.log('🌐 Language:', language);
      console.log('⚙️ System settings:', systemSettings?.aiAssistant);
      
      const systemPrompt = this.buildSystemPrompt(language, systemSettings);
      const contextualPrompt = this.buildContextualPrompt(userMessage, context, language);
      
      console.log('🎯 System prompt length:', systemPrompt.length);
      console.log('📋 Contextual prompt length:', contextualPrompt.length);

      const response = await fetch(`${this.baseUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: this.model,
          prompt: contextualPrompt,
          system: systemPrompt,
          stream: true,
          options: {
            temperature: 0.7,
            top_p: 0.9,
            max_tokens: 500
          }
        })
      });

      if (!response.ok) {
        console.error('❌ Ollama API error:', response.status, response.statusText);
        throw new Error(`Ollama API error: ${response.status}`);
      }

      console.log('✅ Ollama API response received, starting stream processing...');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n').filter(line => line.trim());

          for (const line of lines) {
            try {
              const data = JSON.parse(line);
              if (data.response) {
                fullResponse += data.response;
                onChunk(data.response);
              }
            } catch (e) {
              // Skip invalid JSON lines
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      console.log('🎉 Ollama streaming complete. Total response length:', fullResponse.length);
      return fullResponse;
    } catch (error) {
      console.error('❌ Ollama streaming error:', error);
      throw error;
    }
  }

  // Generate response using RAG
  async generateResponse(userMessage, context = [], language = 'en', systemSettings = null) {
    try {
      const systemPrompt = this.buildSystemPrompt(language, systemSettings);
      const contextualPrompt = this.buildContextualPrompt(userMessage, context, language);

      const response = await fetch(`${this.baseUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: this.model,
          prompt: contextualPrompt,
          system: systemPrompt,
          stream: false,
          options: {
            temperature: 0.7,
            top_p: 0.9,
            max_tokens: 500
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Ollama API error: ${response.status}`);
      }

      const data = await response.json();
      return data.response;
    } catch (error) {
      console.error('Ollama generation error:', error);
      throw error;
    }
  }

  // Build system prompt for Jeje
  buildSystemPrompt(language, systemSettings = null) {
    // Get AI assistant settings
    const aiAssistant = systemSettings?.aiAssistant || {
      name: 'Jeje',
      description: 'Senior AI Advisory Consultant at Scalovate',
      personality: 'Mature, professional advisory consultant specializing in AI adoption and business transformation',
      communicationGuidelines: 'Maintain a mature, consultative approach. Speak as an experienced business advisor.'
    };

    if (language === 'ar') {
      return `أنت ${aiAssistant.name}، مستشار أول للذكاء الاصطناعي في سكالوفيت.

الشخصية والسلوك:
${aiAssistant.personality}

أسلوب التواصل:
${aiAssistant.communicationGuidelines || 'حافظ على نهج استشاري ناضج ومهني. تحدث كمستشار أعمال ذو خبرة. استخدم لغة مهنية دون إفراط في الرسمية.'}

قواعد المحادثة:
- اجعل الردود موجزة (2-3 أسطر كحد أقصى)
- انهِ دائماً بسؤال متابعة ذي صلة
- ركز على فهم احتياجات وتحديات الأعمال
- وجه المناقشات نحو استراتيجيات عملية لتبني الذكاء الاصطناعي

حلول سكالوفيت: البنية التحتية للذكاء الاصطناعي، تطبيقات مخصصة، استشارات الاستراتيجية، نماذج اللغة، الحلول الخاصة، الأتمتة الذكية

بعد 4 تبادلات مفيدة، اقترح التواصل مع المتخصصين: "أرى أن لديك متطلبات محددة تستفيد من مناقشة أكثر تفصيلاً. هل سيكون من المفيد أن أوصلك بأحد زملائي البشريين؟"`;
    } else {
      return `You are ${aiAssistant.name}, a ${aiAssistant.description}.

PERSONALITY & BEHAVIOR:
${aiAssistant.personality}

COMMUNICATION STYLE:
${aiAssistant.communicationGuidelines || 'Maintain a mature, consultative approach. Speak as an experienced business advisor. Use professional language without being overly formal. Demonstrate deep understanding of business challenges and provide thoughtful, strategic insights.'}

CONVERSATION GUIDELINES:
- Keep responses concise (2-3 lines maximum)
- Always end with a relevant follow-up question
- Focus on understanding business needs and challenges
- Guide discussions toward practical AI adoption strategies
- Handle both technical and non-technical conversations professionally

SCALOVATE SOLUTIONS: AI Infrastructure & Implementation, Custom AI Applications, AI Strategy Consulting, LLM Integration, Private AI Solutions, Intelligent Automation, AI Training

LEAD QUALIFICATION: After 4 meaningful exchanges, suggest connecting with human specialists:
"I can see you have specific requirements that would benefit from a more detailed discussion. Would it be helpful if I connected you with one of my human colleagues who can better understand your unique situation and explain our solutions in detail?"

MAINTAIN PROFESSIONAL, CONSULTATIVE TONE - NO OVERLY ENTHUSIASTIC LANGUAGE.`;
    }
  }

  // Build contextual prompt with retrieved information
  buildContextualPrompt(userMessage, context, language) {
    if (context && context.length > 0) {
      // Found relevant content - respond naturally
      const item = context[0];
      if (language === 'ar') {
        return `معلومات ذات صلة:
الموضوع: ${item.subject}
التفاصيل: ${item.details}
الكلمات المفتاحية: ${item.keywords}

سؤال المستخدم: ${userMessage}

أجب بطبيعية كما لو كانت هذه خبرتك الشخصية. لا تذكر "قاعدة البيانات" - تحدث كخبير استشاري. اربط المعلومات باحتياجاتهم التجارية واطرح سؤال متابعة استراتيجي (2-3 أسطر).`;
      } else {
        return `Relevant Information:
Topic: ${item.subject}
Details: ${item.details}
Keywords: ${item.keywords}

User Question: ${userMessage}

Respond naturally as if this is your personal expertise. Don't mention "database" - speak as a knowledgeable consultant. Connect the information to their business needs and ask a strategic follow-up question (2-3 lines).`;
      }
    } else {
      // No specific content - natural conversation
      if (language === 'ar') {
        return `سؤال المستخدم: ${userMessage}

أجب بإيجابية وطبيعية. أظهر اهتماماً حقيقياً بموضوعهم. وجه المحادثة بسلاسة نحو التطبيقات التجارية وحلول سكالوفيت. اسأل عن وضعهم أو صناعتهم (2-3 أسطر فقط).`;
      } else {
        return `User Question: ${userMessage}

Respond positively and naturally. Show genuine interest in their topic. Smoothly guide conversation toward business applications and Scalovate solutions. Ask about their situation or industry (2-3 lines only).`;
      }
    }
  }

  // Retrieve relevant context from knowledge base
  retrieveContext(userMessage, knowledgeBase) {
    const normalizedMessage = userMessage.toLowerCase();
    const relevantContext = [];

    for (const [id, content] of knowledgeBase) {
      const { subject, details, keywords } = content;
      const keywordArray = keywords.split(',').map(k => k.trim().toLowerCase());
      
      // Check for keyword matches
      const hasKeywordMatch = keywordArray.some(keyword => 
        normalizedMessage.includes(keyword) || keyword.includes(normalizedMessage)
      );
      
      // Check for subject match
      const hasSubjectMatch = normalizedMessage.includes(subject.toLowerCase());
      
      // Check for details match
      const hasDetailsMatch = details.toLowerCase().includes(normalizedMessage) || 
                             normalizedMessage.includes(details.toLowerCase().substring(0, 50));
      
      if (hasKeywordMatch || hasSubjectMatch || hasDetailsMatch) {
        relevantContext.push(content);
      }
    }

    // Return top 3 most relevant contexts
    return relevantContext.slice(0, 3);
  }
}

const ollamaService = new OllamaService();
export default ollamaService; 